import {
  ActivityKindEnum,
  AdFieldCurrencyEnum,
  AdFieldOccupancyEnum,
  CompletionStatusEnum,
  CurrencyCommissionEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type {
  PropertyAdAmenityField,
  PropertyAdEditorFormValues,
} from '../../types/propertyAdEditor';

import {
  getPropertySpecificationCompatibility,
  type PropertySpecificationField,
} from './propertyAdSpecifications';
import { getCurrentHandover } from './propertyAdSale';

const clearSaleFields = (values: PropertyAdEditorFormValues): PropertyAdEditorFormValues => ({
  ...values,
  transactionType: null,
  price: null,
  sellingPrice: null,
  completionStatus: null,
  toSeller: null,
  toDeveloper: null,
  postHandover: null,
  handover: null,
});

const clearRentFields = (values: PropertyAdEditorFormValues): PropertyAdEditorFormValues => ({
  ...values,
  defaultRentFrequency: null,
  priceYearly: null,
  priceMonthly: null,
  priceWeekly: null,
  priceDaily: null,
});

const clearDistressOnlyFields = (
  values: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => ({
  ...values,
  sellingPrice: null,
  completionStatus: null,
});

const clearDistressFields = (values: PropertyAdEditorFormValues): PropertyAdEditorFormValues => ({
  ...values,
  sellingPrice: null,
  completionStatus: null,
  toSeller: null,
  toDeveloper: null,
  postHandover: null,
  handover: null,
});

const clearPaymentFields = (values: PropertyAdEditorFormValues): PropertyAdEditorFormValues => ({
  ...values,
  toSeller: null,
  toDeveloper: null,
  postHandover: null,
  handover: null,
});

export const normalizeActivityTransition = (
  previousActivity: ActivityKindEnum | null,
  nextActivity: ActivityKindEnum | null,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  if (previousActivity === nextActivity) {
    return currentValues;
  }

  const nextValues = {
    ...currentValues,
    activityKind: nextActivity,
    ...(nextActivity !== null && {
      currency: currentValues.currency ?? AdFieldCurrencyEnum.AED,
      currencyCommission: currentValues.currencyCommission ?? CurrencyCommissionEnum.AED,
    }),
  };

  if (previousActivity === ActivityKindEnum.Sale && nextActivity === ActivityKindEnum.Rent) {
    return clearSaleFields(nextValues);
  }

  if (previousActivity === ActivityKindEnum.Rent && nextActivity === ActivityKindEnum.Sale) {
    return clearRentFields(nextValues);
  }

  return nextValues;
};

export const normalizeTransactionTransition = (
  previousType: TransactionTypeEnum | null,
  nextType: TransactionTypeEnum | null,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  if (previousType === nextType) {
    return currentValues;
  }

  const nextValues = { ...currentValues, transactionType: nextType };

  if (previousType === TransactionTypeEnum.Ready && nextType === TransactionTypeEnum.Resale) {
    return clearDistressOnlyFields(nextValues);
  }

  if (previousType === TransactionTypeEnum.Resale && nextType === TransactionTypeEnum.Ready) {
    return clearDistressFields(nextValues);
  }

  if (nextType === TransactionTypeEnum.Distress) {
    const distressValues =
      previousType === TransactionTypeEnum.Resale ? clearDistressFields(nextValues) : nextValues;

    return {
      ...distressValues,
      completionStatus: CompletionStatusEnum.OffPlan,
      postHandover: true,
      handover: getCurrentHandover(),
    };
  }

  if (previousType === TransactionTypeEnum.Distress && nextType === TransactionTypeEnum.Ready) {
    return clearDistressFields(nextValues);
  }

  if (previousType === TransactionTypeEnum.Distress && nextType === TransactionTypeEnum.Resale) {
    const resaleValues = clearDistressOnlyFields({
      ...nextValues,
      price: currentValues.sellingPrice ?? currentValues.price,
    });

    return currentValues.completionStatus === CompletionStatusEnum.OffPlan
      ? resaleValues
      : clearPaymentFields(resaleValues);
  }

  return nextValues;
};

export const normalizeCompletionTransition = (
  previousStatus: CompletionStatusEnum | null,
  nextStatus: CompletionStatusEnum | null,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  if (previousStatus === nextStatus) {
    return currentValues;
  }

  const nextValues = { ...currentValues, completionStatus: nextStatus };

  if (
    previousStatus === CompletionStatusEnum.OffPlan &&
    nextStatus === CompletionStatusEnum.Ready
  ) {
    return clearPaymentFields(nextValues);
  }

  return nextValues;
};

export const normalizeOccupancyTransition = (
  previousOccupancy: AdFieldOccupancyEnum | null,
  nextOccupancy: AdFieldOccupancyEnum | null,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  if (previousOccupancy === nextOccupancy) {
    return currentValues;
  }

  return {
    ...currentValues,
    occupancy: nextOccupancy,
    rentedTill:
      previousOccupancy === AdFieldOccupancyEnum.Tenanted &&
      nextOccupancy !== AdFieldOccupancyEnum.Tenanted
        ? null
        : currentValues.rentedTill,
  };
};

export const normalizeAmenityTransition = (
  field: PropertyAdAmenityField,
  nextValue: boolean,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  const normalizedValues = { ...currentValues, [field]: nextValue };

  if (!nextValue) return normalizedValues;

  if (field === 'petsAllowed') {
    normalizedValues.petsNotAllowed = false;
  }
  if (field === 'petsNotAllowed') {
    normalizedValues.petsAllowed = false;
  }

  return normalizedValues;
};

const propertySpecificationFields: PropertySpecificationField[] = [
  'builtUpArea',
  'beds',
  'baths',
  'floor',
  'furniture',
  'renovation',
  'occupancy',
  'rentedTill',
  'view',
];

export const normalizePropertyKindTransition = (
  previousKind: PropertyKindEnum | null,
  nextKind: PropertyKindEnum | null,
  currentValues: PropertyAdEditorFormValues
): PropertyAdEditorFormValues => {
  if (previousKind === nextKind) return currentValues;

  const previousCompatibility = getPropertySpecificationCompatibility(previousKind);
  const nextCompatibility = getPropertySpecificationCompatibility(nextKind);
  const normalizedValues = { ...currentValues, propertyKind: nextKind };

  propertySpecificationFields.forEach((field) => {
    if (!previousCompatibility[field] || nextCompatibility[field]) return;

    switch (field) {
      case 'view':
        normalizedValues.view = [];
        break;
      case 'builtUpArea':
        normalizedValues.builtUpArea = null;
        break;
      case 'beds':
        normalizedValues.beds = null;
        break;
      case 'baths':
        normalizedValues.baths = null;
        break;
      case 'floor':
        normalizedValues.floor = null;
        break;
      case 'furniture':
        normalizedValues.furniture = null;
        break;
      case 'renovation':
        normalizedValues.renovation = null;
        break;
      case 'occupancy':
        normalizedValues.occupancy = null;
        break;
      case 'rentedTill':
        normalizedValues.rentedTill = null;
        break;
    }
  });

  return normalizedValues;
};
